import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import styled from 'styled-components';
import { Trans } from '@lingui/react';
import { EventPaymentSection } from '@ecosystems/events/reserve/EventPaymentSection';
import { LiveGuestAuthSection } from '@ecosystems/events/reserve/LiveGuestAuthSection';
import AccessCodeInput from '@organisms/programs/AccessCodeInput';
import Button from '@components/buttons';
import {
  CodeStatusKind,
  LiveEventKind,
  LiveEventPaymentKind,
  LiveEventQuery,
  ValidateLiveEventCodeQueryResult,
  useValidateLiveEventCodeLazyQuery,
} from '@gql/generated';
import { Box } from '@components/layout/Grid';
import { UserContext } from '@lib/contexts/UserProvider';
import { ApolloError } from '@apollo/client';
import { useRouter } from 'next/router';
import IconTooltip from '@components/IconTooltip';
import { LiveEventFormInfo } from './LiveEventFormInfo';
import Banner from '@organisms/Banner';
import { StartTrialSubscriptionButton } from '@ecosystems/buttons/StartTrialSubscriptionButton';
import LanguageContext from '@lib/contexts/languageContext';

const GuideText = styled.p`
  font-size: 14px;
  line-height: 24px;
  text-wrap: balance;
  margin: 10px 0 15px;
`;

const EnableCodeText = styled.p`
  font-size: 14px;
  font-weight: 600;
  margin: 20px 0 0;
`;

const NoticeBanner = styled(Banner)`
  flex: unset;
  align-items: start;
  margin: 20px 0;
`;

const PremiumLiveBanner = ({ event }) => {
  const router = useRouter();
  const lang = useContext(LanguageContext);
  const [currentUser] = useContext(UserContext);

  return (
    <>
      {event.paymentKind === LiveEventPaymentKind['PremiumLive'] &&
      event.isUserEligible !== true ? (
        <NoticeBanner appearance="warning">
          {currentUser?.subscription.inTrialPeriod === false ? (
            <span className="[&_button]:text-sm">
              <Trans
                id="live.event_page.live_premium_activate_trial_banner"
                components={[
                  <StartTrialSubscriptionButton
                    key="0"
                    className="px-1 text-primary"
                    link
                  >
                    {null}
                  </StartTrialSubscriptionButton>,
                ]}
              />
            </span>
          ) : (
            <span>
              <Trans
                id="live.event_page.live_premium_banner"
                components={[
                  <Link
                    className="px-1 text-primary"
                    key={0}
                    href={`/${lang}/subscription/select?to=${router.asPath}`}
                  >
                    0
                  </Link>,
                ]}
              />
            </span>
          )}
        </NoticeBanner>
      ) : null}
    </>
  );
};

type Props = {
  event: LiveEventQuery['liveEvent'];
  code?: string;
};
export const PaymentForm = (props: Props) => {
  const router = useRouter();
  const [currentUser] = useContext(UserContext);

  const code = router.query?.code as string;
  const { event } = props;
  const { slug } = event;
  const lang = (router?.query.lang || 'se') as string;

  const [accessCode, setAccessCode] = useState(code ?? '');
  const [validationState, setValidationState] = useState<{
    data: ValidateLiveEventCodeQueryResult['data']['validateLiveEventCode'];
    error: ApolloError;
  }>();
  const [showCodeInput, setShowCodeInput] = useState(
    !!code ||
      LiveEventKind['DonationClass'] == event?.kind ||
      event.paymentKind === LiveEventPaymentKind['CampaignLive']
  );
  const [validatingCode, setValidatingCode] = useState(false);
  const [validateLiveEventCode] = useValidateLiveEventCodeLazyQuery({
    variables: {
      slug,
      code,
    },
  });

  const codeValidationText = useMemo(() => {
    // we determin the access code input info text here
    let accessCodeText =
      event?.kind !== LiveEventKind['DonationClass'] ? (
        <>
          <Trans id="live.payment_page.code_input.description" /> &nbsp;
          <IconTooltip
            icon={{
              name: 'ygb-icon-infob',
              fontSize: '18px',
            }}
          >
            <Trans id="live.payment_page.code_input.description_tooltip" />
          </IconTooltip>
        </>
      ) : null;

    if (validationState?.data?.status === CodeStatusKind['Valid']) {
      accessCodeText = <Trans id="live.payment_page.access_code.valid" />;
    } else if (
      [CodeStatusKind['Invalid'], CodeStatusKind['Mismatch']].includes(
        validationState?.data?.status
      ) ||
      validationState?.error?.message
    ) {
      accessCodeText = <Trans id="live.payment_page.access_code.not_valid" />;
    }

    return accessCodeText;
  }, [validationState?.data?.code]);

  const checkAccessCodeValidity = useCallback(async () => {
    setValidatingCode(true);
    const resp = await validateLiveEventCode({
      variables: { slug, code: accessCode },
    });
    setValidationState({
      data: resp.data.validateLiveEventCode,
      error: resp.error,
    });
    if (resp?.data?.validateLiveEventCode?.status === CodeStatusKind['Valid']) {
      router.replace(
        {
          pathname: `/${lang}/live/${slug}/reserve`,
          query: { code: accessCode },
        },
        null,
        { shallow: true }
      );
    }
    setValidatingCode(false);
  }, [accessCode]);

  const clearAccessToken = useCallback(() => {
    setAccessCode('');
    setValidationState(null);
  }, [checkAccessCodeValidity]);

  const handleCodeChange = useCallback((e) => {
    setAccessCode(e.target.value);
  }, []);

  useEffect(() => {
    if (code) {
      checkAccessCodeValidity();
    }
  }, []);

  return (
    <>
      <LiveEventFormInfo event={event} />
      <GuideText>
        {event.kind === LiveEventKind['DonationClass'] ? (
          <Trans
            id="live.payment_page.donation_class_details"
            components={[
              <a
                key="0"
                href="https://startainsamling.wwf.se/campaign/yoga-for-nature/"
                target="_blank"
              ></a>,
              <br key="1" />,
            ]}
          />
        ) : (
          <Trans id="live.payment_page.details" />
        )}
      </GuideText>
      <Box mb={20}>
        {!showCodeInput ? (
          <EnableCodeText>
            <Trans
              id="live.payment_page.code_input.show_text"
              components={[
                <Button
                  key="0"
                  appearance="link"
                  onClick={() => {
                    setShowCodeInput(true);
                  }}
                />,
              ]}
            />
          </EnableCodeText>
        ) : (
          <AccessCodeInput
            inputLabel={<Trans id="live.payment_page.code_input.label" />}
            inputName="codeInput"
            value={accessCode}
            onChange={handleCodeChange}
            clearAccessToken={clearAccessToken}
            loading={validatingCode}
            checkAccessCodeValidity={checkAccessCodeValidity}
            validationState={
              validationState?.data?.code === accessCode
                ? validationState?.data?.status
                : null
            }
            validatedAccessCode={
              validationState?.data?.code === accessCode
                ? !!validationState
                : null
            }
            validationText={
              validationState?.data?.code === accessCode
                ? codeValidationText
                : null
            }
          />
        )}
      </Box>
      <PremiumLiveBanner event={event} />
      {currentUser?.id ? (
        <EventPaymentSection
          event={event}
          accessCodeState={validationState?.data}
          onLiveEventReserved={() => {
            window.location.href = `/${lang}/live/${slug}`;
          }}
        />
      ) : (
        <LiveGuestAuthSection />
      )}
    </>
  );
};
